import axiosInstance from '@/lib/axios/axiosInstance'
import { apiPath } from '@/constants/api-paths'
import type { ResponseContentEntity, ResponseArrayContentEntity } from '@/models/response-types'
import type { Patient, PatientFilters, PatientSignatureResponse } from './patient.types'

const BASE = apiPath.PATIENT

export const patientApi = {
  // GET /api/master/patient — list with filters
  getAll: async (filters?: PatientFilters) => {
    const response = (await axiosInstance.get(BASE, {
      params: filters,
    })) as unknown as ResponseArrayContentEntity<Patient>
    return response.data
  },

  // GET /api/master/patient/{patientId}
  getById: async (uuid: string) => {
    const response = (await axiosInstance.get(
      `${BASE}/${uuid}`,
    )) as unknown as ResponseContentEntity<Patient>
    return response.data
  },

  // GET /api/master/patient/profile
  getProfile: async () => {
    const response = (await axiosInstance.get(
      `${BASE}/profile`,
    )) as unknown as ResponseContentEntity<Patient>
    return response.data
  },

  // GET /api/master/patient/{patientId}/signature — patient's saved signature (base64 / data URL)
  getSignature: async (patientId: string) => {
    const response = (await axiosInstance.get(
      `${BASE}/${patientId}/signature`,
    )) as unknown as ResponseContentEntity<PatientSignatureResponse>
    return response.data
  },

  // POST /api/master/patient
  create: async (payload: Patient) => {
    const response = (await axiosInstance.post(
      BASE,
      payload,
    )) as unknown as ResponseContentEntity<Patient>
    return response.data
  },

  // POST /api/master/patient/{patientUuid}/clinic/{clinicUuid} — assigns clinic + generates MRN.
  // Write-once: fails with 400 if the patient already has an MRN.
  associateClinic: async (patientUuid: string, clinicUuid: string) => {
    const response = (await axiosInstance.post(
      `${BASE}/${patientUuid}/clinic/${clinicUuid}`,
    )) as unknown as ResponseContentEntity<Patient>
    return response
  },

  // PUT /api/master/patient
  update: async (payload: Partial<Patient>) => {
    const response = (await axiosInstance.put(
      BASE,
      payload,
    )) as unknown as ResponseContentEntity<Patient>
    return response.data
  },

  // PUT /api/master/patient/{patientId}/archive-status/{status}
  archive: async (patientId: string, status: boolean) => {
    const response = (await axiosInstance.put(
      `${BASE}/${patientId}/archive-status/${status}`,
    )) as unknown as ResponseContentEntity<Patient>
    return response
  },

  // PUT /api/master/patient/{patientId}/status/{status}
  setActiveStatus: async (patientId: string, status: boolean) => {
    const response = (await axiosInstance.put(
      `${BASE}/${patientId}/status/${status}`,
    )) as unknown as ResponseContentEntity<Patient>
    return response
  },

  // POST /api/master/patient/resend-invitation/patient/{patientId}
  resendInvitation: async (patientId: string) => {
    const response = (await axiosInstance.post(
      `${BASE}/resend-invitation/patient/${patientId}`,
    )) as unknown as ResponseContentEntity<void>
    return response.data
  },

  // PUT /api/master/patient/change-avatar
  changeAvatar: async (userId: string, newAvatar: string) => {
    const response = (await axiosInstance.put(`${BASE}/change-avatar`, {
      newAvatar,
      userId,
    })) as unknown as ResponseContentEntity<Patient>
    return response.data
  },
}
